/**
 * Comprehensive error handling utilities for the tool management system
 * Provides standardized error handling, retry logic, and user-friendly error messages
 */

/**
 * Error types for categorizing different kinds of errors
 */
export const ERROR_TYPES = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  SERVER: 'server',
  NOT_FOUND: 'not_found',
  UNAUTHORIZED: 'unauthorized',
  MISSING_TABLE: 'missing_table',
  SCHEMA_DRIFT: 'schema_drift',
  FIELD_VALIDATION: 'field_validation',
  UNKNOWN: 'unknown'
};

/**
 * Categorize an error based on its characteristics
 * @param {Error|Object} error - The error to categorize
 * @param {Response} response - Optional HTTP response object
 * @returns {string} Error type from ERROR_TYPES
 */
export const categorizeError = (error, response = null) => {
  if (error?.code === 'SCHEMA_DRIFT' || error?.type === ERROR_TYPES.SCHEMA_DRIFT) {
    return ERROR_TYPES.SCHEMA_DRIFT;
  }

  // Network errors
  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    return ERROR_TYPES.NETWORK;
  }
  if (error.message.includes('NetworkError') || error.message.includes('network')) {
    return ERROR_TYPES.NETWORK;
  }

  // HTTP status-based categorization
  if (response) {
    if (response.status === 404) {
      return ERROR_TYPES.NOT_FOUND;
    }
    if (response.status === 401 || response.status === 403) {
      return ERROR_TYPES.UNAUTHORIZED;
    }
    if (response.status >= 500) {
      return ERROR_TYPES.SERVER;
    }
    if (response.status >= 400) {
      return ERROR_TYPES.VALIDATION;
    }
  }

  // Content-based categorization
  if (error.message && typeof error.message === 'string') {
    const message = error.message.toLowerCase();
    
    if (message.includes('table') && (message.includes('not exist') || message.includes('missing'))) {
      return ERROR_TYPES.MISSING_TABLE;
    }
    if (
      message.includes('schema cache')
      || (message.includes('could not find the \'') && message.includes('column'))
      || message.includes('database setup is out of date')
      || message.includes('database schema is out of date')
    ) {
      return ERROR_TYPES.SCHEMA_DRIFT;
    }
    if (message.includes('field') && (message.includes('invalid') || message.includes('not found'))) {
      return ERROR_TYPES.FIELD_VALIDATION;
    }
    if (message.includes('validation') || message.includes('required') || message.includes('invalid')) {
      return ERROR_TYPES.VALIDATION;
    }
  }

  return ERROR_TYPES.UNKNOWN;
};

/**
 * Generate user-friendly error messages based on error type and context
 * @param {Error|Object} error - The error object
 * @param {string} operation - The operation being performed (e.g., 'create', 'update', 'delete')
 * @param {Response} response - Optional HTTP response object
 * @returns {string} User-friendly error message
 */
export const generateErrorMessage = (error, operation = 'operation', response = null) => {
  const errorType = categorizeError(error, response);
  
  switch (errorType) {
    case ERROR_TYPES.NETWORK:
      return `Network error: Unable to connect to the server. Please check your internet connection and try again.`;
    
    case ERROR_TYPES.NOT_FOUND:
      return `The requested resource was not found. It may have been deleted or moved.`;
    
    case ERROR_TYPES.UNAUTHORIZED:
      return `You don't have permission to perform this action. Please check your access rights.`;
    
    case ERROR_TYPES.MISSING_TABLE:
      return `The required data tables are not set up. Please contact your administrator to configure the backend database.`;

    case ERROR_TYPES.SCHEMA_DRIFT: {
      const parts = [error.message || 'Database setup is out of date on this server.'];
      if (error.hint) parts.push(error.hint);
      if (error.action) parts.push(error.action);
      return parts.join(' ');
    }
    
    case ERROR_TYPES.FIELD_VALIDATION:
      return `Field validation failed: ${error.message || 'One or more fields contain invalid data.'}`;
    
    case ERROR_TYPES.VALIDATION:
      // Try to extract specific validation message
      if (error.message && typeof error.message === 'string') {
        return error.message;
      }
      return `Validation error: Please check your input and try again.`;
    
    case ERROR_TYPES.SERVER:
      return `Server error: The server encountered an issue while processing your request. Please try again later.`;
    
    default:
      // For unknown errors, try to use the original message if it's user-friendly
      if (error.message && typeof error.message === 'string' && error.message.length < 200) {
        return error.message;
      }
      return `An unexpected error occurred during ${operation}. Please try again.`;
  }
};

/**
 * Retry configuration for different types of operations
 */
const RETRY_CONFIG = {
  network: { maxRetries: 3, baseDelay: 1000, backoffMultiplier: 2 },
  server: { maxRetries: 2, baseDelay: 2000, backoffMultiplier: 1.5 },
  default: { maxRetries: 1, baseDelay: 1000, backoffMultiplier: 1 }
};

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} Promise that resolves after the delay
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Execute an async operation with retry logic
 * @param {Function} operation - Async function to execute
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries
 * @param {number} options.baseDelay - Base delay between retries in ms
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff
 * @param {Function} options.shouldRetry - Function to determine if error should trigger retry
 * @returns {Promise} Promise that resolves with operation result or rejects with final error
 */
export const withRetry = async (operation, options = {}) => {
  const {
    maxRetries = 1,
    baseDelay = 1000,
    backoffMultiplier = 1.5,
    shouldRetry = (error, response) => {
      const errorType = categorizeError(error, response);
      return errorType === ERROR_TYPES.NETWORK || errorType === ERROR_TYPES.SERVER;
    }
  } = options;

  let lastError;
  let lastResponse;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      lastError = error;
      lastResponse = error.response || null;

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Check if we should retry this error
      if (!shouldRetry(error, lastResponse)) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(backoffMultiplier, attempt);
      await sleep(delay);
    }
  }

  // If we get here, all retries failed
  throw lastError;
};

/**
 * Enhanced fetch wrapper with comprehensive error handling and retry logic
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {Object} retryOptions - Retry configuration
 * @returns {Promise} Promise that resolves with response data or rejects with enhanced error
 */
export const enhancedFetch = async (url, options = {}, retryOptions = {}) => {
  const operation = async () => {
    let response;
    
    try {
      response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });
    } catch (error) {
      // Network error
      const enhancedError = new Error(`Network error: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.type = ERROR_TYPES.NETWORK;
      throw enhancedError;
    }

    // Handle HTTP errors
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }

      const error = new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      error.response = response;
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      if (errorData.code) error.code = errorData.code;
      if (errorData.hint) error.hint = errorData.hint;
      if (errorData.action) error.action = errorData.action;
      if (errorData.details != null) error.details = errorData.details;
      if (errorData.debug != null) error.debug = errorData.debug;
      error.type = categorizeError(error, response);
      throw error;
    }

    // Parse successful response
    try {
      const data = await response.json();
      
      // Check for API-level errors in successful HTTP responses
      if (data.success === false) {
        const error = new Error(data.error || data.message || 'API operation failed');
        error.response = response;
        error.data = data;
        if (data.code) error.code = data.code;
        if (data.hint) error.hint = data.hint;
        if (data.action) error.action = data.action;
        if (data.details != null) error.details = data.details;
        if (data.debug != null) error.debug = data.debug;
        error.type = categorizeError(error, response);
        throw error;
      }
      
      return data;
    } catch (error) {
      if (error.type) {
        // Already an enhanced error from API-level failure
        throw error;
      }
      
      // JSON parsing error
      const parseError = new Error(`Invalid response format: ${error.message}`);
      parseError.originalError = error;
      parseError.response = response;
      parseError.type = ERROR_TYPES.SERVER;
      throw parseError;
    }
  };

  // Determine retry configuration based on operation type
  const errorType = retryOptions.errorType || 'default';
  const config = RETRY_CONFIG[errorType] || RETRY_CONFIG.default;
  
  const finalRetryOptions = {
    ...config,
    ...retryOptions
  };

  return withRetry(operation, finalRetryOptions);
};

/**
 * Normalize tool_kind from API/form values.
 * @returns {'search'|'analytics'|'web_search'|'filter'|'research'|'tool_chain'|'entry_update'|'mcp_call'|'email_draft'|'scheduler'|'http_request'|'webpage_scrape'}
 */
export function normalizeToolKind(raw) {
  const k = String(raw || 'search').toLowerCase();
  if (k === 'analytics') return 'analytics';
  if (k === 'web_search') return 'web_search';
  if (k === 'filter') return 'filter';
  if (k === 'research') return 'research';
  if (k === 'tool_chain' || k === 'toolchain' || k === 'chain') return 'tool_chain';
  if (k === 'entry_update' || k === 'entryupdate' || k === 'update_entry') return 'entry_update';
  if (k === 'entry_create' || k === 'entrycreate' || k === 'create_entry') return 'entry_create';
  if (k === 'mcp_call' || k === 'mcp' || k === 'mcpcall') return 'mcp_call';
  if (k === 'email_draft' || k === 'emaildraft' || k === 'draft_email') return 'email_draft';
  if (k === 'scheduler' || k === 'schedule' || k === 'scheduled') return 'scheduler';
  if (k === 'http_request' || k === 'httprequest' || k === 'http' || k === 'api_request') return 'http_request';
  if (k === 'webpage_scrape' || k === 'webpagescrape' || k === 'page_scrape' || k === 'scrape_page' || k === 'web_scrape') return 'webpage_scrape';
  return 'search';
}

/**
 * Validate tool data with comprehensive validation rules
 * @param {Object} toolData - Tool data to validate
 * @param {boolean} isEdit - Whether this is an edit operation (slug validation differs)
 * @returns {Object} Validation result with { isValid: boolean, errors: Object }
 */
export const validateToolData = (toolData, isEdit = false) => {
  const errors = {};

  // Name validation
  if (!toolData.name || typeof toolData.name !== 'string' || !toolData.name.trim()) {
    errors.name = 'Name is required and cannot be empty';
  } else if (toolData.name.trim().length > 100) {
    errors.name = 'Name must be 100 characters or less';
  }

  // Slug validation
  if (!isEdit) {
    if (!toolData.slug || typeof toolData.slug !== 'string' || !toolData.slug.trim()) {
      errors.slug = 'Slug is required and cannot be empty';
    } else {
      const slug = toolData.slug.trim();
      if (!/^[a-z0-9-_]+$/.test(slug)) {
        errors.slug = 'Slug must contain only lowercase letters, numbers, hyphens, and underscores';
      } else if (slug.length > 50) {
        errors.slug = 'Slug must be 50 characters or less';
      } else if (slug.startsWith('-') || slug.endsWith('-') || slug.startsWith('_') || slug.endsWith('_')) {
        errors.slug = 'Slug cannot start or end with hyphens or underscores';
      }
    }
  }

  // Description validation (optional but has limits)
  if (toolData.description && typeof toolData.description === 'string' && toolData.description.length > 500) {
    errors.description = 'Description must be 500 characters or less';
  }

  const toolKind = normalizeToolKind(toolData.tool_kind);
  const isGrouplessKind =
    toolKind === 'web_search' ||
    toolKind === 'research' ||
    toolKind === 'tool_chain' ||
    toolKind === 'mcp_call' ||
    toolKind === 'email_draft' ||
    toolKind === 'scheduler' ||
    toolKind === 'http_request' ||
    toolKind === 'webpage_scrape';

  if (toolKind === 'mcp_call') {
    if (!toolData.mcp_server_id || !String(toolData.mcp_server_id).trim()) {
      errors.mcp_server_id = 'MCP server is required';
    }
  }

  if (toolKind === 'http_request') {
    const requestUrl = String(toolData.request_url || '').trim();
    if (!requestUrl) {
      errors.request_url = 'Request URL is required';
    } else if (!/^https?:\/\/\S+/i.test(requestUrl.replace(/\{\{[^}]+\}\}/g, 'x'))) {
      errors.request_url = 'Request URL must start with http:// or https://';
    }
    const method = String(toolData.http_method || toolData.method || 'GET').toUpperCase();
    if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'].includes(method)) {
      errors.http_method = 'Invalid HTTP method';
    }
  }

  // Search tools require a search field; analytics/filter may leave it empty
  if (toolKind === 'search') {
    if (!toolData.search_field || typeof toolData.search_field !== 'string' || !toolData.search_field.trim()) {
      errors.search_field = 'Search field is required for search (vector) tools';
    }
  }

  // Search field format validation when provided (not used for web search / research)
  if (!isGrouplessKind && toolData.search_field && typeof toolData.search_field === 'string') {
    const searchField = toolData.search_field.trim();
    if (searchField) {
      const startsRaw = searchField.startsWith('raw.');
      const startsFile = searchField.startsWith('file.');
      const startsIntrinsic = searchField.startsWith('intrinsic.');

      // Allow: raw.*, file.*, intrinsic.*, or output (no prefix)
      if (startsRaw && searchField === 'raw.') {
        errors.search_field = 'Search field cannot be just "raw." - please specify a field name';
      } else if (startsFile && searchField === 'file.') {
        errors.search_field = 'Search field cannot be just "file." - please specify a field name';
      } else if (startsIntrinsic && searchField === 'intrinsic.') {
        errors.search_field = 'Search field cannot be just "intrinsic." - please specify a field name';
      }
    }
  }

  // Return fields validation (not used for web search / research)
  if (!isGrouplessKind) {
  if (toolData.return_fields) {
    if (!Array.isArray(toolData.return_fields)) {
      errors.return_fields = 'Return fields must be an array';
    } else {
      const invalidFields = toolData.return_fields.filter(field => 
        !field || typeof field !== 'string' || !field.trim()
      );
      if (invalidFields.length > 0) {
        errors.return_fields = 'All return fields must be non-empty strings';
      } else if ((toolKind === 'analytics' || toolKind === 'filter' || toolKind === 'entry_update' || toolKind === 'entry_create') && toolData.return_fields.length === 0) {
        const label = toolKind === 'filter'
          ? 'Filter'
          : toolKind === 'entry_update'
            ? 'Entry update'
            : toolKind === 'entry_create'
              ? 'Entry create'
              : 'Analytics';
        errors.return_fields = `${label} tools require at least one allowlisted return field`;
      }
    }
  } else if (toolKind === 'analytics' || toolKind === 'filter' || toolKind === 'entry_update' || toolKind === 'entry_create') {
    const label = toolKind === 'filter'
      ? 'Filter'
      : toolKind === 'entry_update'
        ? 'Entry update'
        : toolKind === 'entry_create'
          ? 'Entry create'
          : 'Analytics';
    errors.return_fields = `${label} tools require at least one allowlisted return field`;
  }
  }

  // Ingestion group validation (required for vector tools, optional for web search / research)
  if (!isGrouplessKind) {
    const slugs = Array.isArray(toolData.ingestion_group_slugs)
      ? toolData.ingestion_group_slugs.filter((s) => String(s || '').trim())
      : [];
    const legacy = String(toolData.ingestion_group_slug || '').trim();
    const count = slugs.length || (legacy ? 1 : 0);
    if (count === 0) {
      errors.ingestion_group_slugs = 'Select at least one ingestion group';
    } else if (count > 5) {
      errors.ingestion_group_slugs = 'Maximum 5 ingestion groups per tool';
    }
  }

  if (toolKind === 'research') {
    const action = String(toolData.research_action || 'start').toLowerCase();
    if (!['start', 'status', 'report'].includes(action)) {
      errors.research_action = 'Research action must be start, status, or report';
    }
  }

  if (toolKind === 'tool_chain') {
    const steps = Array.isArray(toolData.chain_steps) ? toolData.chain_steps : [];
    const validSteps = steps.filter(
      (s) => s && (String(s.tool_slug || '').trim() || String(s.tool_id || '').trim())
    );
    if (validSteps.length === 0) {
      errors.chain_steps = 'Add at least one tool to the chain';
    }
    const policy = String(toolData.failure_policy || 'stop').toLowerCase();
    if (policy !== 'stop' && policy !== 'continue') {
      errors.failure_policy = 'Failure policy must be stop or continue';
    }
  }

  if (toolKind === 'scheduler') {
    const cron = String(toolData.cron_expr || '').trim();
    if (!cron) {
      errors.cron_expr = 'Enter a 5-field cron expression (UTC)';
    } else if (cron.split(/\s+/).length !== 5) {
      errors.cron_expr = 'Use five cron fields: minute hour day-of-month month day-of-week';
    }
    const mode = toolData.scheduler_run_mode || (toolData.prompt_id ? 'prompt' : 'chain');
    if (mode === 'prompt') {
      if (!String(toolData.prompt_id || '').trim()) {
        errors.prompt_id = 'Select a prompt for the scheduler to run';
      }
    } else {
      const steps = Array.isArray(toolData.chain_steps) ? toolData.chain_steps : [];
      const validSteps = steps.filter(
        (s) => s && (String(s.tool_slug || '').trim() || String(s.tool_id || '').trim())
      );
      if (validSteps.length === 0) {
        errors.chain_steps = 'Add at least one tool for the scheduler to run';
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Create a standardized error handler for React components
 * @param {Function} setError - State setter for error messages
 * @param {string} operation - Operation being performed
 * @returns {Function} Error handler function
 */
export const createErrorHandler = (setError, operation = 'operation') => {
  return (error, response = null) => {
    const message = generateErrorMessage(error, operation, response);
    setError(message);
    
    // Log detailed error for debugging
    console.error(`[${operation}] Error:`, {
      message: error.message,
      type: categorizeError(error, response),
      status: response?.status,
      originalError: error
    });
  };
};

/**
 * Clear error message after a delay
 * @param {Function} setError - State setter for error messages
 * @param {number} delay - Delay in milliseconds (default: 5000)
 * @returns {number} Timeout ID that can be used to cancel the clear operation
 */
export const clearErrorAfterDelay = (setError, delay = 5000) => {
  return setTimeout(() => {
    setError('');
  }, delay);
};

const errorUtils = {
  ERROR_TYPES,
  categorizeError,
  generateErrorMessage,
  withRetry,
  enhancedFetch,
  validateToolData,
  normalizeToolKind,
  createErrorHandler,
  clearErrorAfterDelay
};

export default errorUtils;